import { motion } from "framer-motion";
import { Award, Users, Target, Rocket } from "lucide-react";
import Seo from "@/components/common/Seo";
import SectionHeading from "@/components/common/SectionHeading";
import GlowOrbs from "@/components/common/GlowOrbs";
import CountersSection from "@/components/home/CountersSection";
import FinalCTA from "@/components/home/FinalCTA";
import { WHY_US } from "@/lib/data";

const values = [
  { icon: Target, title: "Precision over volume", desc: "Five great matches beat fifty cold applications, every time." },
  { icon: Users, title: "Human-first, AI-augmented", desc: "AI narrows the funnel. Senior US recruiters close the offer." },
  { icon: Award, title: "Long-term relationships", desc: "We plan for OPT → H-1B → Green Card, not just the first offer." },
  { icon: Rocket, title: "Speed without shortcuts", desc: "Every submission is warm, targeted and human-validated." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo title="About Us" description="SKVY Harmony Placement — AI-powered US IT staffing for international students, OPT, CPT and H-1B professionals." path="/about" />

      <section className="relative pt-16 pb-24 overflow-hidden">
        <GlowOrbs />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7 space-y-8">
              <p className="font-inter text-[11px] uppercase tracking-[0.24em] text-[#10B981] font-semibold">About SKVY Harmony Placement</p>
              <h1 className="font-poppins text-5xl sm:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.05]">
                Built for the <span className="text-gradient">next generation</span> of US tech talent.
              </h1>
              <p className="font-inter text-lg text-white/60 max-w-2xl">
                SKVY Harmony Placement is a US-based AI-powered IT staffing and career firm dedicated to international students, OPT/CPT candidates and H-1B professionals. We combine a proprietary AI matching engine with senior US recruiters — so every submission is precise, visa-friendly and closable.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] glass">
                <img
                  src="https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                  alt="Team collaborating"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(10,16,35,0.4))" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <SectionHeading eyebrow="Our mission" title="Turn STEM talent into US tech careers." />
              <p className="font-inter text-white/60 mt-6 leading-relaxed">
                We believe every ambitious international student deserves a fair shot at a US tech career. We invest in AI matching, senior recruiter mentorship and a vetted employer network — so visa status is never the reason a great candidate gets overlooked.
              </p>
            </div>
            <div>
              <SectionHeading eyebrow="Our vision" title="Become the most trusted staffing partner for global tech talent in the US." />
              <p className="font-inter text-white/60 mt-6 leading-relaxed">
                By 2028, we aim to serve 25,000+ international students and H-1B professionals with an NPS above 70 — and to be the default career partner for STEM graduates across US universities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="What we stand for" title="Four values that shape every mandate." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="glass rounded-2xl p-6"
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, rgba(30,58,138,0.4), rgba(16,185,129,0.4))" }}>
                  <v.icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-poppins text-white font-medium text-lg mb-2">{v.title}</p>
                <p className="font-inter text-sm text-white/55">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CountersSection />

      <section className="pb-24" data-testid="why-students-trust">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Why Students Trust Us" title="Six reasons international students choose SKVY Harmony." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {WHY_US.map((w, i) => (
              <div key={w.title} className="glass rounded-2xl p-6 glow-hover" data-testid={`why-us-${i}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, #1E3A8A, #10B981)" }}>
                  <w.icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-poppins text-white font-medium text-lg mb-2">{w.title}</p>
                <p className="font-inter text-sm text-white/55">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
